/* eslint-disable no-nested-ternary */
import { Button, GridGallery, Icon, MediaContainer } from '@design-system';
import { DispatchDate } from '@utils/dispatch';
import { isPrefersReducedMotion } from '@utils/isPrefersReducedMotion';
import { getProductBenefits } from '@utils/product';
import { isProductContentV2 } from '@utils/storyblok/stories';
import classNames from 'classnames';
import { useTranslation } from 'next-i18next';
import React, { useEffect, useMemo, useRef, useState } from 'react';

import { ArtistVideo } from '../../../../components/Content/ArtistVideo';
import { getCustomDimensions } from '../../../../components/Content/Dimensions';
import {
  DynamicLightbox,
  DynamicLightboxProvider,
} from '../../../../components/Content/DynamicLightbox';
import MultiMediaGallery from '../../../../components/Content/MultiMediaGallery/MultiMediaGallery';
import { ProductAccordion } from '../../../../components/Content/ProductAbstract/ProductAccordion';
import { ProductCounterpartsDrawer } from '../../../../components/Content/ProductAbstract/ProductCounterpartsDrawer';
import { ProductHero } from '../../../../components/Content/ProductAbstract/ProductHero';
import { ProductReleaseInfo } from '../../../../components/Content/ProductAbstract/ProductReleaseInfo';
import { ContentBlocks } from '../../../../components/ContentBlocks';
import { Image } from '../../../../components/Image';
import Head from '../../../../components/Layout/Head';
import { ProductRelatedDigitalProducts } from '../../../../components/ProductRelatedDigitalProducts/ProductRelatedDigitalProducts';
import { PRODUCTS_CONTENT_TYPE_BLOCK_V2 } from '../../../../constants';
import { PRODUCT_TAG_NO_COA } from '../../../../constants/product';
import {
  ProductStoryblok,
  RichtextStoryblok,
  VideoStoryblok,
} from '../../../../types/generated-storyblok';
import { Frame, FrameOptionConsolidated } from '../../../../types/Products';
import {
  ProductPurchasingController,
  ShopifyProductPurchaseDetails,
} from '../../../../types/Purchasing';
import { AnalyticsObjectProvider } from '../../../../utils/analytics/object';
import { renderShopifyArtistName } from '../../../../utils/artists';
import { PrivateLinkProduct } from '../../types/PrivateLink';
import { productCTAState } from '../../utils';
import { ProductFrameSelector } from './ProductFrameSelector';

interface ProductDetailsProps {
  canBuyNow: boolean;
  checkoutLoading: boolean;
  frame: Frame | null;
  frameIncluded: boolean;
  frames: FrameOptionConsolidated[] | null;
  onBuyNowClick: () => void;
  onFrameDetailsClick: () => void;
  onClose: () => void;
  privateLinkProduct: PrivateLinkProduct;
  setShowEnquiry: React.Dispatch<React.SetStateAction<boolean>>;
  setFrame: (frame: Frame | null) => void;
  framingOffered?: string;
  isFreeShipping?: boolean;
  tags?: string[];
  dispatchDate: DispatchDate;
  purchasingController: ProductPurchasingController<ShopifyProductPurchaseDetails>;
}

const shopifyToStoryblokDate = (dateString: string | null) => {
  if (dateString === '' || dateString === null) {
    return '';
  }

  const date = new Date(dateString);
  return `${date.toISOString().slice(0, 10)} ${date
    .toISOString()
    .slice(11, 16)}`;
};

const SHOPIFY_TO_STORYBLOK_RELEASE_TYPE_MAPPER = {
  public: 'public-release',
  draw: 'draw-release',
  private: 'private-release',
  timed: 'timed-release',
  auction: '',
  blockchain_auction: '',
  nft_ranked_auction: '',
  nft_giveaway: '',
} as const;

const shopifyToStoryblokReleaseType = (
  key: keyof typeof SHOPIFY_TO_STORYBLOK_RELEASE_TYPE_MAPPER,
) => SHOPIFY_TO_STORYBLOK_RELEASE_TYPE_MAPPER?.[key] ?? '';

export const ProductDetailsModal = ({
  canBuyNow,
  checkoutLoading,
  dispatchDate,
  frame: initialFrame,
  frameIncluded,
  frames,
  framingOffered,
  isFreeShipping,
  onBuyNowClick,
  onClose,
  onFrameDetailsClick,
  privateLinkProduct,
  purchasingController,
  setFrame: setFrameController,
  setShowEnquiry,
  tags,
}: ProductDetailsProps) => {
  const { shopifyProduct, storyblokProduct } = privateLinkProduct;
  const { t } = useTranslation(['product', 'private']);
  const releaseCollectionGalleryRef = useRef<HTMLDivElement>(null);
  const topPositionRef = useRef<HTMLDivElement | null>(null);

  // Keep track of local state since this component is a modal
  const [frame, setFrame] = React.useState<Frame | null>(initialFrame);
  const refMain = useRef<HTMLElement>(null);

  const [
    relatedDigitalProductsDrawerVisible,
    setRelatedDigitalProductsDrawerVisible,
  ] = useState(false);

  const productDetails = storyblokProduct?.details?.[0];

  const images = useMemo(() => {
    if (!frame) {
      return storyblokProduct.images;
    }

    return frame?.images.length > 1
      ? [
          {
            component: 'image' as const,
            image: {
              id: Math.floor(Math.random() * Number.MAX_SAFE_INTEGER),
              alt: frame.title,
              copyright: '',
              filename: frame.images[1].src,
              focus: null,
            },
          },
          ...(storyblokProduct.images?.slice(1) ?? []),
        ]
      : storyblokProduct.images;
  }, [frame, storyblokProduct.images]);

  const { title } = privateLinkProduct.storyblokProduct as ProductStoryblok;

  useEffect(() => {
    refMain.current?.scrollTo(0, 0);
  }, [title]);

  useEffect(() => {
    function handler(ev: KeyboardEvent) {
      if (ev.key !== 'Escape' || ev.altKey || ev.metaKey || ev.ctrlKey) return;

      onClose();

      // Prevent Safari removing the damn window from fullscreen
      ev.preventDefault();
    }

    window.addEventListener('keydown', handler);

    return () => window.removeEventListener('keydown', handler);
  }, [onClose]);

  const [isScrollAtTop, setIsScrollAtTop] = useState(true);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setIsScrollAtTop(true);
          } else {
            setIsScrollAtTop(false);
          }
        });
      },
      {
        root: null,
        rootMargin: '0px',
        threshold: 0,
      },
    );

    if (topPositionRef.current) {
      observer.observe(topPositionRef.current);
    }

    // Cleanup observer on component unmount
    return () => {
      if (topPositionRef.current) {
        observer.unobserve(topPositionRef.current);
      }
    };
  }, []);

  if (!shopifyProduct) return null;

  const benefits = getProductBenefits(storyblokProduct, purchasingController);

  const ctaState = productCTAState(privateLinkProduct);

  const heroMedia = images?.[0];
  const galleryMedia = images?.slice(1);
  const heroMediaVideo = (() => {
    if (heroMedia?.component !== 'Video') return null;

    const vimeoId = heroMedia.vimeo.vimeo_oembed.response.video_id.toString();

    const media = { type: 'VIMEO' as const, id: vimeoId };

    return media;
  })();

  const shopifyProductPrice = Number(shopifyProduct.price);
  const totalPrice = frame
    ? shopifyProductPrice +
      frame.variant.consolidatedVariants.reduce(
        (acc, item) => acc + item.price,
        0,
      )
    : shopifyProductPrice;

  const artistsStoryblok = isProductContentV2(storyblokProduct)
    ? storyblokProduct.artists
    : storyblokProduct.productArtist;

  const productArtists = artistsStoryblok.reduce<{ name: string }[]>(
    (acc, { artist }) => {
      if (!artist || typeof artist === 'string') {
        return acc;
      }
      const { name } = artist;
      const isEstate = artist.content?.isEstate;
      const estateName = artist.content?.estateName;
      acc.push({ name: isEstate && estateName ? estateName : name });

      return acc;
    },
    [],
  );
  const productPrice = shopifyProduct.enquireOnlyNoPrice
    ? 'On Request'
    : totalPrice;

  const fromTheArtistVideo =
    storyblokProduct.component === 'product'
      ? (storyblokProduct.fromTheArtistContainer?.find(
          (item) => item.component === 'Video',
        ) as VideoStoryblok)
      : undefined;

  const handleFrameChange = (frameSelected: Frame | null) => {
    if (checkoutLoading) return;

    setFrame(frameSelected);
    setFrameController(frameSelected);
  };
  const { relatedDigitalProducts } = storyblokProduct;

  return (
    <AnalyticsObjectProvider
      name="private product details"
      properties={{
        'product_title': shopifyProduct.title,
        'product_artist': renderShopifyArtistName(shopifyProduct.artist),
        'product_medium': shopifyProduct.medium,
        'product_price': shopifyProduct.price,
        'product_edition_size': shopifyProduct.editionSize,
        'shopify_product_id': shopifyProduct.id,
        'shopify_product_variant_id': privateLinkProduct.shopifyVariantId,
        'product_can_be_framed': Boolean(
          purchasingController.frameOptions?.length,
        ),
        'product_frame_upgrade_available': Boolean(frames?.length),
        'product_frame_chosen': Boolean(frame),
        'product_framed_by_default': frameIncluded,
      }}
      sendViewedEvent
    >
      {(emitter) => (
        <main
          ref={refMain}
          className="theme-light min-w-screen h-[95%] overflow-y-scroll rounded-t-lg bg-white"
        >
          <Head title={`${shopifyProduct.title} by ${shopifyProduct.artist}`} />

          <div className="relative h-auto w-screen">
            <div ref={topPositionRef} className="absolute top-0" />
            <div className="sticky top-0 z-50 flex">
              <div
                className={classNames(
                  'absolute flex w-full justify-end p-4 transition-all duration-300 md:p-6 md:pr-14',
                  isScrollAtTop
                    ? 'bg-transparent'
                    : 'bg-white md:bg-transparent',
                )}
              >
                <Button
                  hideLabel
                  icon="ic_close"
                  label="Close"
                  onClick={() => {
                    emitter('close button', 'clicked');
                    onClose();
                  }}
                  size="sm"
                  variant="tertiary"
                />
              </div>
            </div>

            <div className="flex flex-col place-items-center justify-center gap-y-9 pb-9 md:pt-9">
              <DynamicLightboxProvider>
                <div className="w-full max-w-xl">
                  {heroMedia?.component === 'image' && (
                    <DynamicLightbox
                      key={heroMedia.image.id}
                      item={{
                        copyright: heroMedia.image.copyright,
                        key: heroMedia.image.id,
                        media: {
                          type: 'IMAGE',
                          src: heroMedia.image.filename,
                          alt: heroMedia.image.alt,
                        },
                      }}
                    >
                      {(lightbox) => (
                        <button
                          aria-label="Open in lightbox"
                          className="group w-full"
                          onClick={() =>
                            lightbox.openOnSlide(heroMedia.image.id)
                          }
                          type="button"
                        >
                          <Image
                            alt={heroMedia.image?.alt ?? ''}
                            focus={heroMedia.image.focus}
                            hoverEffect
                            shape="rounded"
                            size={{
                              sm: { ratio: '7/8', span: 12 },
                              md: { ratio: '7/8', span: 8 },
                              lg: { ratio: '7/8', span: 6 },
                            }}
                            src={heroMedia.image.filename}
                          />
                        </button>
                      )}
                    </DynamicLightbox>
                  )}

                  {heroMedia?.component === 'Video' && heroMediaVideo && (
                    <DynamicLightbox
                      key={heroMedia.coverImage.id}
                      item={{
                        copyright: heroMedia.coverImage.copyright,
                        key: Number(heroMediaVideo.id),
                        media: heroMediaVideo,
                      }}
                    >
                      {(lightbox) => (
                        <button
                          aria-label="Open in lightbox"
                          className="group w-full"
                          onClick={() =>
                            lightbox.openOnSlide(Number(heroMediaVideo.id))
                          }
                          type="button"
                        >
                          <MediaContainer
                            duration={
                              heroMedia.vimeo.vimeo_oembed.response.duration
                            }
                            mediaComponent={
                              <Image
                                alt={heroMedia.coverImage?.alt ?? ''}
                                focus={heroMedia.coverImage.focus}
                                hoverEffect
                                size={{
                                  sm: { ratio: '7/8', span: 12 },
                                  md: { ratio: '7/8', span: 6 },
                                  lg: { ratio: '7/8', span: 4 },
                                }}
                                src={heroMedia.coverImage.filename}
                              />
                            }
                            title={heroMedia.title}
                            type="Video"
                          />
                        </button>
                      )}
                    </DynamicLightbox>
                  )}
                </div>

                <div className="m-auto flex w-full max-w-lg flex-col gap-y-9 px-website">
                  <ProductHero
                    artists={productArtists}
                    price={Number(productPrice)}
                    title={shopifyProduct.title}
                  />

                  <div className="flex shrink-0 justify-center empty:hidden">
                    <ProductFrameSelector
                      frame={frame}
                      frameIncluded={frameIncluded}
                      frames={frames}
                      onFrameChange={(f) => {
                        refMain?.current?.scroll({
                          behavior: isPrefersReducedMotion()
                            ? 'instant'
                            : 'smooth',
                          top: 0,
                        });

                        handleFrameChange(f);
                      }}
                      product={
                        storyblokProduct as unknown as ProductType | null
                      }
                    />
                  </div>

                  <ProductReleaseInfo
                    available={ctaState !== 'SOLD_OUT'}
                    closingDate={
                      shopifyProduct.closingDate &&
                      shopifyToStoryblokDate(shopifyProduct.closingDate)
                    }
                    dimensions={productDetails?.dimensions?.[0]}
                    dispatchDate={dispatchDate}
                    isFramed={Boolean(frame)}
                    editionFormat={
                      storyblokProduct.component === 'product_v2'
                        ? storyblokProduct.editionFormat
                        : undefined
                    }
                    editionNote={
                      storyblokProduct.component === 'product'
                        ? storyblokProduct?.editionNote
                        : undefined
                    }
                    editionSize={shopifyProduct.editionSize}
                    framingOffered={framingOffered}
                    freeShipping={isFreeShipping}
                    hideShippingEstimate={
                      storyblokProduct.component === 'product'
                        ? false
                        : Boolean(storyblokProduct.hideShippingEstimate)
                    }
                    medium={storyblokProduct.medium}
                    onEditionSizeAnchorClick={() => {
                      emitter('', 'private room - collection clicked');
                      releaseCollectionGalleryRef.current?.scrollIntoView({
                        behavior: isPrefersReducedMotion()
                          ? 'instant'
                          : 'smooth',
                      });
                    }}
                    releaseCollectionGallery={
                      storyblokProduct.releaseCollectionGallery
                    }
                    releaseNote={
                      storyblokProduct.releaseNote as RichtextStoryblok
                    }
                    releaseType={shopifyToStoryblokReleaseType(
                      shopifyProduct.releaseType,
                    )}
                    tags={tags || []}
                  />

                  {relatedDigitalProducts &&
                    relatedDigitalProducts?.length > 0 && (
                      <>
                        <ProductRelatedDigitalProducts
                          image={
                            Array.isArray(relatedDigitalProducts[0]?.media)
                              ? relatedDigitalProducts[0].media[0].component ===
                                'image'
                                ? relatedDigitalProducts[0]?.media[0]?.image
                                : relatedDigitalProducts[0]?.media[0]
                                    ?.coverImage
                              : relatedDigitalProducts[0]?.media
                          }
                          label={t('digitalProducts.tile.description')}
                          onClick={() => {
                            setRelatedDigitalProductsDrawerVisible(true);
                            // emitter('', 'paired NFT clicked');
                          }}
                        />
                        <ProductCounterpartsDrawer
                          counterparts={relatedDigitalProducts}
                          isVisible={relatedDigitalProductsDrawerVisible}
                          overlayOtherDrawers
                          setIsVisible={setRelatedDigitalProductsDrawerVisible}
                        />
                      </>
                    )}
                </div>

                {(galleryMedia?.length ?? 0) ? (
                  <div className="m-auto w-full max-w-5xl px-website">
                    <div className="grid grid-cols-2 gap-3">
                      {galleryMedia?.map((i) => {
                        if (i.component === 'image') {
                          return (
                            <DynamicLightbox
                              item={{
                                copyright: i.image.copyright,
                                key: i.image.id,
                                media: {
                                  type: 'IMAGE',
                                  src: i.image.filename,
                                  alt: i.image.alt,
                                },
                              }}
                            >
                              {(lightbox) => (
                                <div
                                  aria-label="Open in lightbox"
                                  className="group w-full"
                                  onClick={() =>
                                    lightbox.openOnSlide(i.image.id)
                                  }
                                  onKeyDown={() =>
                                    lightbox.openOnSlide(i.image.id)
                                  }
                                  role="button"
                                  tabIndex={0}
                                >
                                  <MediaContainer
                                    mediaComponent={
                                      <Image
                                        alt={i.image?.alt ?? ''}
                                        focus={i.image.focus}
                                        hoverEffect
                                        shape="rounded"
                                        size={{
                                          sm: { ratio: '1/1', span: 6 },
                                          md: { ratio: '1/1', span: 4 },
                                          lg: { ratio: '1/1', span: 4 },
                                        }}
                                        src={i.image.filename}
                                      />
                                    }
                                    type="Image"
                                  />
                                </div>
                              )}
                            </DynamicLightbox>
                          );
                        }

                        if (
                          i.component === 'Video' &&
                          i.vimeo.vimeo_oembed !== null
                        ) {
                          const vimeoId =
                            i.vimeo.vimeo_oembed.response.video_id.toString();
                          const media = {
                            type: 'VIMEO' as const,

                            id: vimeoId,
                          };

                          return (
                            <DynamicLightbox
                              item={{
                                copyright: i.coverImage.copyright,
                                key: Number(media.id),
                                media,
                              }}
                            >
                              {(lightbox) => (
                                <div
                                  aria-label="Open in lightbox"
                                  className="group w-full"
                                  onClick={() =>
                                    lightbox.openOnSlide(Number(media.id))
                                  }
                                  onKeyDown={() =>
                                    lightbox.openOnSlide(Number(media.id))
                                  }
                                  role="button"
                                  tabIndex={0}
                                >
                                  <MediaContainer
                                    duration={
                                      i.vimeo.vimeo_oembed.response.duration
                                    }
                                    mediaComponent={
                                      <Image
                                        alt={i.coverImage?.alt ?? ''}
                                        focus={i.coverImage.focus}
                                        hoverEffect
                                        shape="rounded"
                                        size={{
                                          sm: { ratio: '1/1', span: 6 },
                                          md: { ratio: '1/1', span: 6 },
                                          lg: { ratio: '1/1', span: 6 },
                                        }}
                                        src={i.coverImage.filename}
                                      />
                                    }
                                    title={title}
                                    type="Video"
                                  />
                                </div>
                              )}
                            </DynamicLightbox>
                          );
                        }

                        return null;
                      })}
                    </div>
                  </div>
                ) : null}
              </DynamicLightboxProvider>

              {storyblokProduct.component === PRODUCTS_CONTENT_TYPE_BLOCK_V2 ? (
                <>
                  {productDetails ? (
                    <div className="m-auto w-full max-w-lg px-website">
                      <ProductAccordion
                        authentication={productDetails.authentication}
                        benefits={benefits}
                        customDimensions={getCustomDimensions(productDetails)}
                        description={productDetails.description}
                        eligibility={productDetails.eligibility}
                        finishing={productDetails.finishing}
                        framingDetails={
                          storyblokProduct?.framing?.[0] ||
                          productDetails.framingOptions
                        }
                        framingOptions={purchasingController.frameOptions}
                        isFrameIncluded={frameIncluded}
                        medium={productDetails.medium}
                        noCoa={
                          purchasingController.product?.tags.includes(
                            PRODUCT_TAG_NO_COA,
                          ) ?? false
                        }
                        onFramingDetailsClick={() => onFrameDetailsClick()}
                        packagingDetails={storyblokProduct?.packaging?.[0]}
                        productId={storyblokProduct.shopifyId}
                        releaseType={shopifyToStoryblokReleaseType(
                          shopifyProduct.releaseType,
                        )}
                        tags={tags}
                      />
                    </div>
                  ) : null}

                  <ContentBlocks
                    content={storyblokProduct?.artistContent ?? []}
                  />
                </>
              ) : (
                <>
                  {productDetails ? (
                    <div className="m-auto w-full max-w-lg px-website">
                      <ProductAccordion
                        authentication={productDetails.authentication}
                        benefits={benefits}
                        customDimensions={getCustomDimensions(productDetails)}
                        description={productDetails.description}
                        eligibility={productDetails.eligibility}
                        finishing={productDetails.finishing}
                        framingDetails={productDetails.framingOptions}
                        isFrameIncluded={frameIncluded}
                        medium={productDetails.medium}
                        noCoa={
                          purchasingController.product?.tags.includes(
                            PRODUCT_TAG_NO_COA,
                          ) ?? false
                        }
                        onFramingDetailsClick={() => onFrameDetailsClick()}
                        productId={shopifyProduct.id}
                        releaseType={storyblokProduct.releaseType}
                        tags={tags}
                      />
                    </div>
                  ) : null}

                  {fromTheArtistVideo ? (
                    <div className="m-auto w-full max-w-5xl px-website">
                      <ArtistVideo
                        blok={fromTheArtistVideo}
                        coverImageSize={{
                          sm: { ratio: '3/2', span: 12 },
                          md: { ratio: '3/2', span: 6 },
                          lg: { ratio: '3/2', span: 4 },
                        }}
                        showTitle={false}
                      />
                    </div>
                  ) : null}

                  {storyblokProduct.behindTheScenesContainer &&
                  storyblokProduct.behindTheScenesContainer.length > 0 ? (
                    <div className="m-auto w-full max-w-3xl px-website">
                      <MultiMediaGallery
                        config={
                          storyblokProduct
                            .behindTheScenesContainer[0] as unknown as MultiMediaCarousel
                        }
                      />
                    </div>
                  ) : null}
                </>
              )}

              {storyblokProduct.releaseCollectionGallery &&
              storyblokProduct.releaseCollectionGallery.length > 0 ? (
                <section
                  ref={releaseCollectionGalleryRef}
                  className="m-auto w-full max-w-5xl space-y-4 px-website text-md"
                >
                  <h2 className="text-neutral-4 md:text-lg">
                    {t('releaseCollectionGalleryHeading')}
                  </h2>
                  <GridGallery
                    images={storyblokProduct.releaseCollectionGallery}
                  />
                </section>
              ) : null}

              <div className="m-auto flex w-full max-w-lg flex-col items-start gap-1 px-website">
                <span className="text-neutral-4">
                  {t('private:details.questions.heading')}
                </span>

                <a
                  className="link flex gap-1"
                  href="mailto:support@avantarte.com"
                  rel="noreferrer"
                  target="_blank"
                >
                  <Icon className="mr-1" name="ic_mail" />
                  <span>{t('private:details.questions.emailUsButton')}</span>
                </a>

                <a
                  className="link flex gap-1"
                  href="https://avantarte.com/support/faq"
                  rel="noreferrer"
                  target="_blank"
                >
                  <Icon className="mr-1" name="ic_arrow_top_right" />
                  <span>{t('private:details.questions.faqsButton')}</span>
                </a>
              </div>
            </div>

            <div className="pointer-events-none sticky bottom-0 z-20 w-full bg-gradient-to-t from-white to-transparent pb-9 pt-4">
              <div className="flex justify-center">
                {(ctaState === 'AVAILABLE' || ctaState === 'SOLD_OUT') && (
                  <div className="pointer-events-auto flex md:w-48">
                    <Button
                      disabled={
                        !privateLinkProduct.availableForSale || !canBuyNow
                      }
                      fullWidth
                      label={
                        privateLinkProduct.availableForSale
                          ? t('private:details.buyButton')
                          : t('private:details.soldOutButton')
                      }
                      loading={checkoutLoading}
                      onClick={() => {
                        emitter('buy now', 'clicked');
                        onBuyNowClick();
                      }}
                      variant="primary"
                    />
                  </div>
                )}

                {ctaState === 'ENQUIRY' && (
                  <div className="pointer-events-auto flex md:w-48">
                    <Button
                      fullWidth
                      label={t('private:details.enquiryButton')}
                      onClick={() => {
                        emitter('enquire now', 'clicked');
                        setShowEnquiry(true);
                      }}
                      variant="primary"
                    />
                  </div>
                )}
              </div>
            </div>
          </div>
        </main>
      )}
    </AnalyticsObjectProvider>
  );
};
